import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  Collapse,
  IconButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { inject, observer } from "mobx-react";
import {
  Add,
  ExpandMore,
  ExpandLess,
  ChevronLeft,
  FilterList,
  Search,
  Clear,
  DragHandle,
  HighlightOff,
} from "@mui/icons-material";

import { OrganizationSvg } from "components/icons";
import {
  ButtonGroup,
  DatePicker,
  DatePickerDates,
  Filter,
  MultiSelect,
  TableFields,
  Button,
  Select,
} from "components/ui";
import { getDatesFromFilterValue, setFilterValueByDates, sortFilterEntities, without } from "tools";
import {
  DB_FILTER_CONDITION_TYPE,
  FILTER_PAGES,
  FilterConditionKey,
  FilterData,
  FilterDataDates,
  FilterDataItem,
  FilterEntity,
  FilterItem,
  InventorySearchData,
  InventorySearchDataTag,
  InventoryTagValue,
  Account,
} from "store/types";
import { RootStore } from "store/root.store";
import { getRows, mapToFilterEntities } from "components/ui/JsonViewer/jsonViewer.utils";

import InventoryTag from "./inventoryTag";

type InventoryFilterProps = {
  store?: RootStore;
  selectedAccounts: string[];
  searchData: InventorySearchData;
  filter: FilterDataItem;
  setFilter: (filter: FilterDataItem) => void;
  onSelectedAccountsChange?: (accounts: string[]) => void;
  filterSidebarOpen?: boolean;
  setFilterSidebarOpen?: (open: boolean) => void;
  filterSidebarWidth?: number;
  onResizeMouseDown?: (e: React.MouseEvent) => void;
};

const fields = [
  {
    name: "Status",
    property: "asset_status",
  },
  {
    name: "Tags",
    property: "tags",
  },
  {
    name: "Types",
    property: "types",
  },
  {
    name: "Locations",
    property: "locations",
  },
  {
    name: "Date",
    property: "dates",
  },
];

const assetStatusOptions = [
  { value: "active", component: "Active" },
  { value: "deleted", component: "Deleted" },
  { value: "all", component: "All" },
];

const dateFields = [
  {
    id: "created_at",
    name: "Created at",
  },
  {
    id: "updated_at",
    name: "Updated at",
  },
];

const ButtonGroupComponent = ({
  exclude,
  onChange,
  disabled,
}: {
  exclude: boolean;
  onChange: (exclude: boolean) => void;
  disabled?: boolean;
}) => (
  <ButtonGroup
    inTable
    exclusive
    disabled={disabled}
    items={[
      { value: DB_FILTER_CONDITION_TYPE.INCLUDE, component: "Include" },
      { value: DB_FILTER_CONDITION_TYPE.EXCLUDE, component: "Exclude" },
    ]}
    value={exclude ? DB_FILTER_CONDITION_TYPE.EXCLUDE : DB_FILTER_CONDITION_TYPE.INCLUDE}
    onChange={(val: string) => onChange(val === DB_FILTER_CONDITION_TYPE.EXCLUDE)}
  />
);

const InventoryFilter = ({
  store,
  searchData,
  selectedAccounts,
  filter,
  setFilter,
  onSelectedAccountsChange,
  filterSidebarOpen,
  setFilterSidebarOpen,
  filterSidebarWidth = 320,
  onResizeMouseDown,
}: InventoryFilterProps) => {
  const [detailsFilter, setDetailsFilter] = useState<FilterDataItem>(
    (filter as FilterData).items?.find((f) => (f as FilterData).type === FILTER_PAGES.INVENTORY_DETAILS) ||
      store!.filter.getDefaultFilter(FILTER_PAGES.INVENTORY_DETAILS),
  );
  const [detailsFilterEntities, setDetailsFilterEntities] = useState<FilterEntity>();
  const [detailsFilterFields, setDetailsFilterFields] = useState<TableFields[]>([]);
  const [defaultTags, setDefaultTags] = useState<InventorySearchDataTag[]>([]);
  const [tags, setTags] = useState<InventoryTagValue[]>([{ tag: null, value: null }]);
  const [assetStatus, setAssetStatus] = useState<string>("active");

  // Use parent state if provided, otherwise use internal state
  const [internalSidebarOpen, setInternalSidebarOpen] = useState<boolean>(true);
  const sidebarOpen = setFilterSidebarOpen ? filterSidebarOpen! : internalSidebarOpen;
  const setSidebarOpen = setFilterSidebarOpen || setInternalSidebarOpen;
  const [accountsExpanded, setAccountsExpanded] = useState<boolean>(true);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);
  const [providerExpanded, setProviderExpanded] = useState<Record<string, boolean>>({});
  const [orgExpanded, setOrgExpanded] = useState<Record<string, boolean>>({});

  // Account search state
  const [accountSearchQuery, setAccountSearchQuery] = useState<string>("");

  // Organizations state
  const [organizations, setOrganizations] = useState<Record<string, string>>({});

  // Load organizations on component mount
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const orgs = await store?.accounts.getOrganizations();
        if (orgs) {
          const orgMap: Record<string, string> = {};
          orgs.forEach((org) => {
            orgMap[org.id] = org.name;
          });
          setOrganizations(orgMap);
        }
      } catch {
        // Failed to load organizations - continue silently
      }
    };

    loadOrganizations();
  }, [store?.accounts]);

  const getDefaultTags = useCallback(async () => {
    // Don't fetch tags if no accounts are selected
    if (selectedAccounts.length === 0) return;

    const data = await store?.inventory.getInventoryTags(selectedAccounts, {
      keyPrefix: "",
    });
    setDefaultTags(data as InventorySearchDataTag[]);
  }, [store?.inventory, selectedAccounts]);

  useEffect(() => {
    getDefaultTags();
  }, [getDefaultTags]);

  useEffect(() => {
    const entities = mapToFilterEntities(searchData.details.flatMap((c) => getRows(c).filter((r) => !r.onlyJson)));

    const detailFields = Object.keys(entities).map((property) => ({ property, name: property }));
    setDetailsFilterFields(detailFields);

    // Update the shared store with the fields
    if (store?.inventory) {
      store.inventory.setDataStatsFields(detailFields);
    }

    setDetailsFilterEntities(sortFilterEntities(entities));
  }, [searchData.details, store?.inventory]);

  const changeValue = useCallback(
    (field: string, value: string | string[], condition: FilterConditionKey) => {
      const filterData = filter as FilterData;
      const currentItems = JSON.parse(JSON.stringify(filterData.items));
      const currentItem = currentItems.find((f: FilterItem) => f.field === field);

      if (currentItem) {
        currentItem[Array.isArray(value) ? "values" : "value"] = value;
        currentItem.condition = condition;
      } else {
        currentItems.push({
          ...(Array.isArray(value)
            ? {
                value: "",
                values: value || [],
              }
            : {
                value: value || "",
                values: [],
              }),
          field,
          condition,
        } as FilterItem);
      }

      const newFilter: FilterData = {
        ...filterData,
        items: currentItems.filter(
          (c: FilterDataItem) =>
            (c as FilterData).type === FILTER_PAGES.INVENTORY_DETAILS ||
            (c as FilterItem).value ||
            (c as FilterItem).values?.length,
        ),
      };
      setFilter(newFilter);
    },
    [filter, setFilter],
  );

  const getField = useCallback(
    (field: string) => (filter as FilterData).items?.find((f) => (f as FilterItem).field === field) as FilterItem,
    [filter],
  );

  const getTagsValue = useCallback(() => {
    try {
      return JSON.parse(getField("tags")?.value as string);
    } catch {
      return {};
    }
  }, [getField]);

  const setDates = (dates: DatePickerDates) => {
    const filterData = filter as FilterData;
    setFilter({
      ...filterData,
      dates: {
        field: filterData.dates?.field || "updated_at",
        condition: filterData.dates?.condition || "IS",
        ...setFilterValueByDates(dates),
      },
    });
  };

  const setDatesByField = (field: string) => {
    const filterData = filter as FilterData;
    setFilter({
      ...filterData,
      dates: {
        ...(filterData.dates as FilterDataDates),
        field,
      },
    });
  };

  // Helper function to group accounts by provider and organization
  const getGroupedAccounts = useCallback(() => {
    const accounts = store?.accounts.activeAccounts || [];
    const grouped: Record<string, Record<string, Account[]>> = {};

    // Filter accounts based on search query
    const filteredAccounts = accounts.filter((account) => {
      if (!accountSearchQuery.trim()) return true;

      const query = accountSearchQuery.toLowerCase();
      const accountName = account.name?.toLowerCase() || "";
      const orgName = account.organizational_unit_id?.toLowerCase() || "";
      const accountId = account.provider_account_id?.toLowerCase() || "";

      return accountName.includes(query) || orgName.includes(query) || accountId.includes(query);
    });

    filteredAccounts.forEach((account) => {
      const provider = account.provider || "Unknown";
      const orgId = account.organizational_unit_id || "No Organization";

      if (!grouped[provider]) {
        grouped[provider] = {};
      }
      if (!grouped[provider][orgId]) {
        grouped[provider][orgId] = [];
      }
      grouped[provider][orgId].push(account);
    });

    // Sort providers alphabetically (AWS, GCP, others...)
    const sortedGrouped: Record<string, Record<string, Account[]>> = {};
    const sortedProviders = Object.keys(grouped).sort();

    sortedProviders.forEach((provider) => {
      sortedGrouped[provider] = grouped[provider];
    });

    return sortedGrouped;
  }, [store?.accounts.activeAccounts, accountSearchQuery]);

  const toggleProvider = (provider: string) => {
    setProviderExpanded((prev) => ({ ...prev, [provider]: !prev[provider] }));
  };

  const toggleOrg = (provider: string, orgId: string) => {
    const uniqueOrgKey = `${provider}_${orgId}`;
    setOrgExpanded((prev) => ({ ...prev, [uniqueOrgKey]: !prev[uniqueOrgKey] }));
  };

  const isAccountSelected = (accountId: string) => selectedAccounts.includes(accountId);

  const handleAccountToggle = (accountId: string) => {
    if (!onSelectedAccountsChange) return;

    const newSelectedAccounts = isAccountSelected(accountId)
      ? selectedAccounts.filter((id) => id !== accountId)
      : [...selectedAccounts, accountId];

    onSelectedAccountsChange(newSelectedAccounts);
  };

  // Handle provider selection (select/deselect all accounts under a provider)
  const handleProviderToggle = (provider: string, accounts: Account[]) => {
    if (!onSelectedAccountsChange) return;

    const providerAccountIds = accounts.map((account) => account.id);
    const allSelected = providerAccountIds.every((id) => selectedAccounts.includes(id));

    let newSelectedAccounts: string[];
    if (allSelected) {
      // Deselect all accounts in this provider
      newSelectedAccounts = selectedAccounts.filter((id) => !providerAccountIds.includes(id));
    } else {
      // Select all accounts in this provider
      const newIds = providerAccountIds.filter((id) => !selectedAccounts.includes(id));
      newSelectedAccounts = [...selectedAccounts, ...newIds];
    }

    onSelectedAccountsChange(newSelectedAccounts);
  };

  // Handle organization selection (select/deselect all accounts under an org)
  const handleOrgToggle = (visibleOrgAccounts: Account[]) => {
    if (!onSelectedAccountsChange || visibleOrgAccounts.length === 0) return;

    // Get the provider and orgId from the visible accounts
    const { provider, organizational_unit_id: orgId } = visibleOrgAccounts[0];

    // Get ALL accounts for this organization from the original data, not just filtered ones
    const allAccounts = store?.accounts.activeAccounts || [];
    const allOrgAccounts = allAccounts.filter(
      (account) => account.provider === provider && account.organizational_unit_id === orgId,
    );

    const allOrgAccountIds = allOrgAccounts.map((account) => account.id);
    const allSelected = allOrgAccountIds.every((id) => selectedAccounts.includes(id));

    let newSelectedAccounts: string[];
    if (allSelected) {
      // Deselect all accounts in this org
      newSelectedAccounts = selectedAccounts.filter((id) => !allOrgAccountIds.includes(id));
    } else {
      // Select all accounts in this org (including those filtered out by search)
      const newIds = allOrgAccountIds.filter((id) => !selectedAccounts.includes(id));
      newSelectedAccounts = [...selectedAccounts, ...newIds];
    }

    onSelectedAccountsChange(newSelectedAccounts);
  };

  // Check if all accounts under a provider are selected
  const isProviderSelected = (accounts: Account[]) => {
    const accountIds = accounts.map((account) => account.id);
    return accountIds.every((id) => selectedAccounts.includes(id));
  };

  // Check if some (but not all) accounts under a provider are selected
  const isProviderIndeterminate = (accounts: Account[]) => {
    const accountIds = accounts.map((account) => account.id);
    const selectedCount = accountIds.filter((id) => selectedAccounts.includes(id)).length;
    return selectedCount > 0 && selectedCount < accountIds.length;
  };

  // Check if all accounts under an org are selected (including those filtered out by search)
  const isOrgSelected = (visibleAccounts: Account[]) => {
    if (visibleAccounts.length === 0) return false;

    // Get the provider and orgId from the visible accounts
    const { provider, organizational_unit_id: orgId } = visibleAccounts[0];

    // Get ALL accounts for this organization from the original data
    const allAccounts = store?.accounts.activeAccounts || [];
    const allOrgAccounts = allAccounts.filter(
      (account) => account.provider === provider && account.organizational_unit_id === orgId,
    );

    const allOrgAccountIds = allOrgAccounts.map((account) => account.id);
    return allOrgAccountIds.every((id) => selectedAccounts.includes(id));
  };

  // Check if some (but not all) accounts under an org are selected (including those filtered out by search)
  const isOrgIndeterminate = (visibleAccounts: Account[]) => {
    if (visibleAccounts.length === 0) return false;

    // Get the provider and orgId from the visible accounts
    const { provider, organizational_unit_id: orgId } = visibleAccounts[0];

    // Get ALL accounts for this organization from the original data
    const allAccounts = store?.accounts.activeAccounts || [];
    const allOrgAccounts = allAccounts.filter(
      (account) => account.provider === provider && account.organizational_unit_id === orgId,
    );

    const allOrgAccountIds = allOrgAccounts.map((account) => account.id);
    const selectedCount = allOrgAccountIds.filter((id) => selectedAccounts.includes(id)).length;
    return selectedCount > 0 && selectedCount < allOrgAccountIds.length;
  };

  const setDetailsFilterHandler = (newFilter: FilterDataItem) => {
    if (detailsFilter === newFilter) return;

    setDetailsFilter(newFilter);

    const filterData = filter as FilterData;
    const items =
      filterData.items?.filter((c: FilterDataItem) => (c as FilterData).type !== FILTER_PAGES.INVENTORY_DETAILS) || [];

    items.push({
      ...(without(newFilter, ["dates"]) as FilterDataItem),
      type: FILTER_PAGES.INVENTORY_DETAILS,
    });

    const updatedFilter: FilterData = { ...filterData, items };
    setFilter(updatedFilter);
  };

  const addTag = () => setTags((curr) => [...curr, { tag: null, value: null }]);

  const setTagsValue = (val: InventoryTagValue[]) => {
    const tagValue = val
      .filter((t) => t.tag && t.value)
      .reduce((acc, { tag, value }) => ({ ...acc, [tag as string]: value }), {});

    const tagField = getField("tags");

    const value = Object.keys(tagValue).length ? JSON.stringify(tagValue) : "";

    changeValue("tags", value, tagField?.condition || "IS");
  };

  useEffect(() => {
    const newTags = Object.entries(getTagsValue()).map(([tag, value]) => ({
      tag: tag as string,
      value: value as string,
    }));

    setTags(newTags.length ? newTags : [{ tag: null, value: null }]);
  }, [getTagsValue]);

  const handleAssetStatusChange = useCallback(
    (status: string) => {
      setAssetStatus(status);
      changeValue("asset_status", status, "IS");
    },
    [changeValue],
  );

  useEffect(() => {
    const statusField = getField("asset_status");
    if (statusField?.value) {
      setAssetStatus(statusField.value as string);
    }
  }, [getField]);

  // Initialize expanded states for providers and organizations
  useEffect(() => {
    const grouped = getGroupedAccounts();

    // Initialize provider expanded state only for new providers
    setProviderExpanded((prev) => {
      const newProviderExpanded = { ...prev };
      Object.keys(grouped).forEach((provider) => {
        if (!(provider in newProviderExpanded)) {
          newProviderExpanded[provider] = true; // Start expanded for new providers
        }
      });
      return newProviderExpanded;
    });

    // Initialize org expanded state only for new orgs
    setOrgExpanded((prev) => {
      const newOrgExpanded = { ...prev };
      Object.entries(grouped).forEach(([provider, orgsByProvider]) => {
        Object.keys(orgsByProvider).forEach((orgId) => {
          const uniqueOrgKey = `${provider}_${orgId}`;
          if (!(uniqueOrgKey in newOrgExpanded)) {
            newOrgExpanded[uniqueOrgKey] = true; // Start expanded for new orgs
          }
        });
      });
      return newOrgExpanded;
    });
  }, [getGroupedAccounts]);

  const groupedAccounts = getGroupedAccounts();

  return (
    <Box
      display="flex"
      height={{ xs: "auto", md: "calc(100vh - 120px)" }} // Auto height on mobile, fixed height on desktop
      position="relative"
      sx={{
        overflow: "hidden",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: {
            xs: "calc(100vw - 32px)", // Account for page margins on mobile
            md: sidebarOpen ? filterSidebarWidth : 40,
          },
          minWidth: {
            xs: "calc(100vw - 32px)",
            md: sidebarOpen ? filterSidebarWidth : 40,
          },
          transition: "width 0.1s ease, min-width 0.1s ease",
          height: {
            xs: sidebarOpen ? "auto" : "60px",
            md: "calc(100vh - 120px)",
          },
          maxHeight: {
            xs: sidebarOpen ? "300px" : "60px",
            md: "calc(100vh - 120px)",
          },
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          borderRadius: 2,
          position: "relative",
          top: "auto",
          zIndex: 1, // Lower z-index to stay behind navigation
          display: "flex",
          flexDirection: "column",
          marginRight: { xs: 0, md: 2 },
          marginBottom: { xs: sidebarOpen ? 2 : 1, md: 0 }, // Small margin when collapsed
        }}
      >
        <Box
          display="flex"
          justifyContent={sidebarOpen ? "center" : "center"}
          alignItems="center"
          p={1}
          borderBottom={sidebarOpen ? "1px solid" : "none"}
          borderColor="divider"
          position="relative"
        >
          {sidebarOpen && (
            <Typography variant="h6" fontWeight="bold">
              Filters
            </Typography>
          )}
          <Tooltip title={sidebarOpen ? "" : "Open Filters"} placement="right">
            <IconButton
              onClick={() => setSidebarOpen(!sidebarOpen)}
              size="small"
              sx={{
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                ...(sidebarOpen && {
                  position: "absolute",
                  right: 8,
                }),
              }}
            >
              {sidebarOpen ? <ChevronLeft /> : <FilterList />}
            </IconButton>
          </Tooltip>
        </Box>

        {sidebarOpen && (
          <Box
            p={2}
            sx={{
              height: "calc(100% - 60px)", // Account for header height
              overflow: "auto",
              overflowX: "hidden",
              flex: 1,
            }}
          >
            {/* Accounts Section */}
            <Box mb={2}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ cursor: "pointer", mb: 1 }}
                onClick={() => setAccountsExpanded(!accountsExpanded)}
              >
                <Typography variant="h6" fontWeight="bold">
                  Accounts ({selectedAccounts.length}/{store?.accounts.activeAccounts.length || 0})
                </Typography>
                <IconButton size="small">{accountsExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
              </Box>

              <Collapse in={accountsExpanded}>
                <Box ml={1} sx={{ maxHeight: "33vh", overflowY: "auto" }}>
                  {/* Search Box */}
                  <Box mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search"
                      value={accountSearchQuery}
                      onChange={(e) => setAccountSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: accountSearchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setAccountSearchQuery("")} sx={{ p: 0.5 }}>
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 1 }}
                    />
                  </Box>

                  {Object.entries(groupedAccounts).map(([provider, orgsByProvider]) => {
                    const allProviderAccounts = Object.values(orgsByProvider).flat();
                    return (
                      <Box key={provider} mb={1}>
                        <Box
                          display="flex"
                          alignItems="center"
                          sx={{ cursor: "pointer", mb: 0.5 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProvider(provider);
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={isProviderSelected(allProviderAccounts)}
                            indeterminate={isProviderIndeterminate(allProviderAccounts)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleProviderToggle(provider, allProviderAccounts);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            sx={{ mr: 0.5, p: 0.5 }}
                          />
                          <IconButton size="small" sx={{ mr: 0.5 }}>
                            {providerExpanded[provider] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {provider.toUpperCase()}
                          </Typography>
                        </Box>

                        <Collapse in={providerExpanded[provider]}>
                          <Box ml={2}>
                            {Object.entries(orgsByProvider).map(([orgId, accounts]) => (
                              <Box key={orgId} mb={1}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  sx={{ cursor: "pointer", mb: 0.5 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleOrg(provider, orgId);
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={isOrgSelected(accounts)}
                                    indeterminate={isOrgIndeterminate(accounts)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleOrgToggle(accounts);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    sx={{ mr: 0.5, p: 0.5 }}
                                  />
                                  <IconButton size="small" sx={{ mr: 0.5 }}>
                                    {orgExpanded[`${provider}_${orgId}`] ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontSize="0.8rem"
                                    display="flex"
                                    alignItems="center"
                                    fontWeight="bold"
                                  >
                                    {orgId !== "No Organization" && (
                                      <OrganizationSvg
                                        style={{ width: "1rem", height: "1rem", marginRight: "0.25rem" }}
                                      />
                                    )}
                                    {orgId === "No Organization" ? "No Organization" : organizations[orgId] || orgId}
                                  </Typography>
                                </Box>

                                <Collapse in={orgExpanded[`${provider}_${orgId}`]}>
                                  <Box ml={2}>
                                    {accounts.map((account) => (
                                      <FormControlLabel
                                        key={account.id}
                                        control={
                                          <Checkbox
                                            checked={isAccountSelected(account.id)}
                                            onChange={() => handleAccountToggle(account.id)}
                                            size="small"
                                          />
                                        }
                                        label={
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" sx={{ display: "flex", fontSize: "0.875rem" }}>
                                              {account.name}
                                            </Typography>
                                            <Typography
                                              variant="main"
                                              color="text.secondary"
                                              sx={{
                                                backgroundColor: "background.default",
                                                padding: "2px 6px",
                                                borderRadius: 0.5,
                                                fontSize: "0.75rem",
                                                display: "flex",
                                              }}
                                            >
                                              {account.provider_account_id}
                                            </Typography>
                                          </Box>
                                        }
                                        sx={{ display: "flex", width: "100%", mr: 0 }}
                                      />
                                    ))}
                                  </Box>
                                </Collapse>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>

            <Divider />

            {/* Filters Section */}
            <Box mt={2}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ cursor: "pointer", mb: 1 }}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                <Typography variant="h6" fontWeight="bold">
                  Metadata
                </Typography>
                <IconButton size="small">{filtersExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
              </Box>

              <Collapse in={filtersExpanded}>
                <Box ml={1}>
                  {/* Status Filter */}
                  <Box mb={2}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Status
                    </Typography>
                    <ButtonGroup
                      inTable
                      exclusive
                      value={assetStatus}
                      onChange={handleAssetStatusChange}
                      items={assetStatusOptions}
                    />
                  </Box>

                  {/* Tags Filter */}
                  {!!defaultTags.length && (
                    <Box mb={2}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Tags
                        </Typography>
                        <Button
                          disabled={tags.some((t) => !t.tag || !t.value)}
                          onClick={addTag}
                          icon={Add}
                          onlyIcon
                          smallWidth
                          inTable
                        />
                      </Box>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {tags.map((t, index) => (
                          <InventoryTag
                            key={`${t.tag}-${t.value}`}
                            index={index}
                            tags={tags}
                            setTags={setTags}
                            setTagsValue={setTagsValue}
                            selectedAccounts={selectedAccounts}
                            defaultTags={defaultTags}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Other Filters */}
                  {fields
                    .filter((f) => f.property !== "asset_status" && f.property !== "tags")
                    .map((field) => (
                      <Box key={field.property} mb={2}>
                        {field.property === "dates" ? (
                          <>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {field.name}
                              </Typography>
                              {(filter as FilterData).dates && (
                                <HighlightOff
                                  onClick={() => {
                                    const filterData = filter as FilterData;
                                    setFilter({
                                      ...filterData,
                                      dates: undefined,
                                    });
                                  }}
                                  sx={{
                                    zIndex: 1,
                                    color: "text.secondary",
                                    backgroundColor: "background.paper",
                                    cursor: "pointer",
                                  }}
                                />
                              )}
                            </Box>
                            <Box mb={1}>
                              <Select
                                fullWidth
                                inTable
                                smallWidth
                                value={(filter as FilterData).dates?.field || "updated_at"}
                                list={dateFields}
                                onChange={setDatesByField}
                              />
                            </Box>
                            <DatePicker
                              setFullWidth
                              range
                              dates={getDatesFromFilterValue((filter as FilterData).dates)}
                              setDates={setDates}
                            />
                            {(filter as FilterData).dates && (
                              <Box mt={1}>
                                <ButtonGroupComponent
                                  exclude={(filter as FilterData).dates?.condition === "IS_NOT"}
                                  onChange={(exclude: boolean) => {
                                    const filterData = filter as FilterData;
                                    setFilter({
                                      ...filterData,
                                      dates: {
                                        ...filterData.dates!,
                                        condition: exclude ? "IS_NOT" : "IS",
                                      },
                                    });
                                  }}
                                />
                              </Box>
                            )}
                          </>
                        ) : (
                          <>
                            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                              {field.name}
                            </Typography>
                            <MultiSelect
                              limitTags={2}
                              fullWidth
                              multiple
                              placeholder={`Select ${field.name}`}
                              value={getField(field.property)?.values || []}
                              list={
                                (searchData[field.property as keyof InventorySearchData] as string[])?.map((t) => ({
                                  id: t,
                                  name: t,
                                })) || []
                              }
                              onChange={(val) =>
                                changeValue(
                                  field.property,
                                  val?.id || val,
                                  getField(field.property)?.condition || "IS_ONE_OF",
                                )
                              }
                            />
                            <Box mt={1}>
                              <ButtonGroupComponent
                                disabled={!getField(field.property)?.values?.length}
                                exclude={getField(field.property)?.condition === "IS_NOT_ONE_OF"}
                                onChange={(exclude) =>
                                  changeValue(
                                    field.property,
                                    getField(field.property)?.value as string,
                                    exclude ? "IS_NOT_ONE_OF" : "IS_ONE_OF",
                                  )
                                }
                              />
                            </Box>
                          </>
                        )}
                      </Box>
                    ))}
                </Box>
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Advanced Filters */}
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Fields
            </Typography>
            <Filter
              nested
              type={FILTER_PAGES.INVENTORY_DETAILS}
              fields={detailsFilterFields}
              currentFilter={detailsFilter}
              setCurrentFilter={setDetailsFilterHandler}
              entities={detailsFilterEntities}
              maxNestedElements={1}
              fieldOnlyOnce
            />
          </Box>
        )}

        {/* Resize Handle */}
        {sidebarOpen && onResizeMouseDown && (
          <Box
            onMouseDown={onResizeMouseDown}
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%) translateX(50%)",
              width: 8,
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "col-resize",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              transition: "border-color 0.2s, background-color 0.2s",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
              },
              zIndex: 10,
            }}
          >
            <DragHandle
              sx={{
                transform: "rotate(90deg)",
                color: "text.secondary",
                opacity: 0.75,
                fontSize: 20,
                transition: "opacity 0.2s, color 0.2s",
                // marginLeft: 1,
                // marginRight: 1,
                "&:hover": {
                  opacity: 1,
                  color: "primary.main",
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default inject("store")(observer(InventoryFilter));
